import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DefaultValues, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import {
  ChConcludeBillingSchemaType,
  createChConcludeBillingSchema,
} from "@/modules/common/billing/components/billing-form/ch/conclude/schema/ch-conclude-billing-schema"
import {
  DEFAULT_NOT_BILLABLE_REASON,
  EMPLOYER_INSURANCE,
} from "@/modules/common/billing/components/billing-form/constants"
import {
  DeBillingSchema,
  DeBillingSchemaType,
} from "@/modules/common/billing/components/billing-form/de/schema/de-billing-schema"
import { mapBillingFormToCollectInput } from "@/modules/common/billing/components/billing-form/mapBillingFormToCollectInput"
import { BillingAttributeFragment$key } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/BillingAttributeFragment.graphql"
import { CollectBillingDataInput } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useCollectBillingDataMutation.graphql"
import { BillingAttributeFragment } from "@/modules/patients/detail/conclude/billing-section/services/BillingAttributeFragment"
import { ConcludeAside, ConcludeAsideRef } from "@/modules/patients/detail/conclude/conclude-aside"
import { DocumentViewer } from "@/modules/patients/detail/conclude/document-viewer"
import { AgentFeedbackDialog } from "@/modules/patients/detail/conclude/feedback-dialog/agent-feedback-dialog"
import {
  incrementConcludeCountWhenRelevantForFeedbackDialog,
  useIsRelevantForFeedbackDialog,
  useShouldShowFeedbackDialog,
} from "@/modules/patients/detail/conclude/feedback-dialog/useShouldShowFeedbackDialog"
import { CarePlanViewer } from "@/modules/patients/detail/progress-note/documents/care-plan-summary-document-preview"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import {
  getChProgressNotesByAppointmentId,
  getDeProgressNotesByAppointmentId,
} from "@/modules/patients/detail/store/useDocumentStore"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useEditableProgressNotesInContextOfAppointment } from "@/modules/patients/hooks/useEditableProgressNotesInContextOfAppointment"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { getNumberOfDocumentsToSend } from "@/modules/patients/libs/documents-to-send"
import { getInsureeInfo } from "@/modules/patients/libs/get-insuree-info"
import { isMissingPatientContactInformation } from "@/modules/patients/libs/is-missing-patient-contact-information"
import { shouldSendToPartner } from "@/modules/patients/libs/send-to-partner"
import { GynaecologyFragment$key } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { AgentConsultationFeedbackInput } from "@/modules/patients/services/__generated__/useConcludeAppointmentMutation.graphql"
import { GynaecologyFragment } from "@/modules/patients/services/GynaecologyFragment"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { useConcludeAppointmentMutation } from "@/modules/patients/services/useConcludeAppointmentMutation"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { usePatientGynaecology } from "@/modules/patients/services/usePatientGynaecology"
import { useReConcludeAppointmentMutation } from "@/modules/patients/services/useReConcludeAppointmentMutation"
import {
  CARE_PLAN_SUMMARY,
  ChProgressNoteDocument,
  DeProgressNoteDocument,
} from "@/modules/patients/types/ProgressNoteDocument"

type ConcludeDialogProps = {
  onOpenChange: (open: boolean) => void
  previousProgressNoteBillingDataRef?: BillingAttributeFragment$key | null
}

type BillingSchemaType = DeBillingSchemaType & ChConcludeBillingSchemaType

const ConcludeDialog = ({ onOpenChange, previousProgressNoteBillingDataRef }: ConcludeDialogProps) => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const country = useCountryParam()
  const setTriggeredValidation = useConcludeDialogStoreContext((state) => state.actions.setTriggeredValidation)
  const appointmentId = useAppointmentParam()

  if (!appointmentId) {
    throw new Error("Appointment ID is required to conclude.")
  }

  const chFormattedProgressNotesWithAppointmentInfo = getChProgressNotesByAppointmentId(appointmentId)
  const deFormattedProgressNotesWithAppointmentInfo = getDeProgressNotesByAppointmentId(appointmentId)
  const setSelectedDocument = useConcludeDialogStoreContext((state) => state.actions.setSelectedDocument)
  const setIsConcluded = useConcludeDialogStoreContext((state) => state.actions.setIsConcluded)
  const [concludeAppointmentMutation, isConcludingAppointment] = useConcludeAppointmentMutation()
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)
  const patientFileInfo = usePatientFileContext((state) => state.patientFileInfo)
  const findNextDocument = useConcludeDialogStoreContext((state) => state.actions.findNextDocument)
  const concludeAsideRef = useRef<ConcludeAsideRef>(null)
  const [viewerKey, setViewerKey] = useState(0)
  const [hasUnSignedOrUnApprovedDocument, setHasUnSignedOrUnApprovedDocument] = useState(true)
  const [patientData] = usePatientByIdQuery(patientId, country, appointmentId)
  const contactChannel = patientData.patientById?.contactChannel
  const noPatientContactInfo = !patientFileInfo?.emailAddress || !patientFileInfo.phoneNumber
  const isMissingPatientContactInfo = isMissingPatientContactInformation(contactChannel)
  const patientFragmentData = useFragment<PatientFragment$key>(PatientFragment, patientData.patientById)
  const patientAge = patientFragmentData?.age?.years
  const gynaecologyRef = usePatientGynaecology(patientId, country)
  const gynaecology = useFragment<GynaecologyFragment$key>(GynaecologyFragment, gynaecologyRef)
  const isPatientPregnant = gynaecology?.pregnancy === "PREGNANT"
  const patientOrganisationKey =
    getInsureeInfo(patientData.patientById?.insurees)?.coverage?.product?.organisation?.key ?? null

  const contextAppointment = useContextAppointment()
  const isPsychosocialConsultation = contextAppointment?.service === "PSYCHOSOCIAL"
  const canSendToPartner = shouldSendToPartner(contextAppointment)
  const [reConcludeAppointment, isReConcludingAppointment] = useReConcludeAppointmentMutation()
  const shouldShowFeedbackDialog = useShouldShowFeedbackDialog()
  const isRelevantForFeedbackDialog = useIsRelevantForFeedbackDialog()
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const documents = useMemo(() => {
    if (country === "DE")
      return Array.from(deFormattedProgressNotesWithAppointmentInfo ?? [])
        .map((progressNote: [string, Omit<DeProgressNoteDocument, "medicalReportAttachments">]) => {
          return Object.values(progressNote[1]).filter((item) => item && item.type !== "medicalReportAttachments")
        })
        ?.reduce((prevDocument, currentDocument) => [...prevDocument, ...currentDocument], [])

    return Array.from(chFormattedProgressNotesWithAppointmentInfo ?? [])
      .map((progressNote: [string, Omit<ChProgressNoteDocument, "medicalReportAttachments">]) => {
        return Object.values(progressNote[1]).filter((item) => item && item.type !== "medicalReportAttachments")
      })
      ?.reduce((prevDocument, currentDocument) => [...prevDocument, ...currentDocument], [])
  }, [country, chFormattedProgressNotesWithAppointmentInfo, deFormattedProgressNotesWithAppointmentInfo])

  useEffect(() => {
    const hasUnSignedOrUnApprovedDocument = documents.some((document) => {
      return document && !(document.signedAt ?? document.signedBy)
    })
    setHasUnSignedOrUnApprovedDocument(hasUnSignedOrUnApprovedDocument)
  }, [documents])

  const concludableProgressNotes = useEditableProgressNotesInContextOfAppointment()
  const { numberOfDocumentsToSend, numberOfDocumentsToSendToPatient, numberOfDocumentsRequiringPatientChannel } =
    getNumberOfDocumentsToSend(concludableProgressNotes)
  const hasDocumentsToSend = numberOfDocumentsToSend > 0
  const hasDocumentsToSendToPatient = numberOfDocumentsToSendToPatient > 0
  const hasDocumentsRequiringPatientChannel = numberOfDocumentsRequiringPatientChannel > 0

  const persistedBillingData = useFragment<BillingAttributeFragment$key>(
    BillingAttributeFragment,
    contextAppointment?.billingAttribute,
  )

  const followUpProgressNoteBillingData = useFragment<BillingAttributeFragment$key>(
    BillingAttributeFragment,
    previousProgressNoteBillingDataRef,
  )
  const billingData = persistedBillingData ?? followUpProgressNoteBillingData

  const BillingSchema = country === "CH" ? createChConcludeBillingSchema() : DeBillingSchema

  const existingBillingData: DefaultValues<BillingSchemaType> | null = billingData
    ? {
        ...billingData,
        isNotBillable: contextAppointment?.service === "ADMINISTRATIVE_REQUEST" ? false : billingData.isNotBillable,
        reason: billingData.reason ?? undefined,
        treatmentReason:
          billingData.treatmentReason ??
          (isPsychosocialConsultation && !billingData.isNotBillable ? "SICKNESS" : undefined),
        notBillableReason: billingData.notBillableReason ?? DEFAULT_NOT_BILLABLE_REASON,
        isVisualSymptomExamination: billingData.isVisualSymptomExamination ?? false,
        currentGestationWeek: billingData.currentGestationWeek ? billingData.currentGestationWeek.toString() : null,
        organisationId: billingData.isEmployer ? EMPLOYER_INSURANCE : (billingData.organisationId ?? undefined),
        dateOfAccident: billingData.dateOfAccident ? new Date(billingData.dateOfAccident) : undefined,
        accidentReference: billingData.accidentReference ?? undefined,
        caseReference: billingData.ivgCaseReference ?? undefined,
      }
    : null

  const form = useForm<BillingSchemaType>({
    mode: "all",
    resolver: zodResolver(BillingSchema),
    defaultValues: existingBillingData ?? {
      isNotBillable: false,
      notBillableReason: DEFAULT_NOT_BILLABLE_REASON,
      reason: "",
      treatmentReason: isPsychosocialConsultation ? "SICKNESS" : undefined,
    },
  })

  const handleSuccessfulSigning = (currentId: string) => {
    const nextDocument = findNextDocument(currentId, country, appointmentId)
    if (nextDocument) {
      const { type, id, progressNoteId, fileId, signedAt } = nextDocument
      const renderFn =
        type === CARE_PLAN_SUMMARY
          ? () => <CarePlanViewer id={id} progressNoteId={progressNoteId} fileId={fileId} signedAt={signedAt} />
          : undefined

      concludeAsideRef.current?.handleDocumentSelection({
        ...nextDocument,
        renderFn,
      })
    } else setHasUnSignedOrUnApprovedDocument(false)
  }

  const onCloseDialog = () => {
    setSelectedDocument(null)
    onOpenChange(false)
  }

  const mapToBillingInput = useCallback(
    (billingData: BillingSchemaType) =>
      mapBillingFormToCollectInput(billingData, {
        appointmentId,
        country,
        patientAge,
        patientOrganisationKey,
        isPatientPregnant,
      }),
    [appointmentId, country, isPatientPregnant, patientAge, patientOrganisationKey],
  )

  const handleReConclude = useCallback(
    async (collectBillingInput: CollectBillingDataInput) => {
      let progressNoteIds: string[] = []

      switch (country) {
        case "CH":
          progressNoteIds = chFormattedProgressNotesWithAppointmentInfo
            ? [...chFormattedProgressNotesWithAppointmentInfo.keys()]
            : []
          break
        case "DE":
          progressNoteIds = deFormattedProgressNotesWithAppointmentInfo
            ? [...deFormattedProgressNotesWithAppointmentInfo.keys()]
            : []
          break
      }

      await reConcludeAppointment({
        id: appointmentId,
        collectBillingInput,
        progressNoteIds,
        isEditWithin24Hours: true,
      })
    },
    [
      appointmentId,
      chFormattedProgressNotesWithAppointmentInfo,
      country,
      deFormattedProgressNotesWithAppointmentInfo,
      reConcludeAppointment,
    ],
  )

  const performConclude = useCallback(
    async (agentFeedback: AgentConsultationFeedbackInput | null) => {
      const collectBillingInput = mapToBillingInput(form.getValues())

      if (contextAppointment?.isCompleted === false) {
        await concludeAppointmentMutation({
          id: appointmentId,
          shouldIncreaseMissedCall: false,
          collectBillingInput,
          agentFeedback,
        })

        incrementConcludeCountWhenRelevantForFeedbackDialog(isRelevantForFeedbackDialog)
      } else {
        await handleReConclude(collectBillingInput)
      }
      onOpenChange(false)
      setSelectedDocument(null)
      setIsConcluded()
      setShowFeedbackDialog(false)
      toast({
        description: t("patient.conclude.sendMessageSuccessfully"),
      })
    },
    [
      appointmentId,
      concludeAppointmentMutation,
      contextAppointment?.isCompleted,
      form,
      handleReConclude,
      isRelevantForFeedbackDialog,
      mapToBillingInput,
      onOpenChange,
      setIsConcluded,
      setSelectedDocument,
      t,
    ],
  )

  const handleFeedbackSubmit = useCallback(
    async (feedback: AgentConsultationFeedbackInput) => {
      await performConclude(feedback)
    },
    [performConclude],
  )

  const handleSend = useCallback(
    async (_data: BillingSchemaType) => {
      if (hasUnSignedOrUnApprovedDocument) {
        setTriggeredValidation()
      } else {
        if (shouldShowFeedbackDialog) {
          setShowFeedbackDialog(true)
        } else {
          await performConclude(null)
        }
      }
    },
    [hasUnSignedOrUnApprovedDocument, performConclude, setTriggeredValidation, shouldShowFeedbackDialog],
  )

  const handleConcludeNoDocumentsToSendToPatient = useCallback(
    async (_data: BillingSchemaType) => {
      if (hasUnSignedOrUnApprovedDocument) {
        setTriggeredValidation()
      } else {
        if (shouldShowFeedbackDialog) {
          setShowFeedbackDialog(true)
        } else {
          await performConclude(null)
        }
      }
    },
    [hasUnSignedOrUnApprovedDocument, performConclude, setTriggeredValidation, shouldShowFeedbackDialog],
  )

  const sendDocumentButton = () => (
    <Button
      className="px-4 py-2"
      data-testid="send-documents-button"
      onClick={() => {
        FaroInsightsWrapper.pushEvent("check_out_send_documents")
        form.handleSubmit(handleSend)()
      }}
      isLoading={isConcludingAppointment || isReConcludingAppointment}
      disabled={isMissingPatientContactInfo && !canSendToPartner && hasDocumentsRequiringPatientChannel}>
      {t("patient.conclude.sendDocuments")}
    </Button>
  )

  const concludeButton = () => (
    <Button
      className="px-4 py-2"
      data-testid="conclude-no-documents-button"
      onClick={() => {
        FaroInsightsWrapper.pushEvent("check_out_no_documents")
        form.handleSubmit(handleConcludeNoDocumentsToSendToPatient)()
      }}
      isLoading={isConcludingAppointment || isReConcludingAppointment}>
      {t("patient.header.conclude")}
    </Button>
  )

  const renderConcludeButton = () => {
    if (noPatientContactInfo) {
      if (hasDocumentsToSendToPatient) {
        return sendDocumentButton()
      } else {
        return concludeButton()
      }
    } else {
      if (hasDocumentsToSend) {
        return sendDocumentButton()
      } else {
        return concludeButton()
      }
    }
  }

  const getHeaderInfo = () => {
    if (noPatientContactInfo) {
      if (hasDocumentsToSendToPatient) {
        return t("patient.header.concludeAndSendDocuments", { count: numberOfDocumentsToSend })
      } else {
        return t("patient.header.conclude")
      }
    } else {
      if (hasDocumentsToSend) {
        return t("patient.header.concludeAndSendDocuments", { count: numberOfDocumentsToSend })
      } else {
        return t("patient.header.conclude")
      }
    }
  }

  return (
    <>
      <Dialog open onOpenChange={onCloseDialog}>
        <FormProvider {...form}>
          <DialogContentCustom
            onPointerDownOutside={(e) => e.preventDefault()}
            data-testid="conclude-dialog-content"
            className="flex h-[calc(100vh-120px)] flex-col">
            <DialogHeader className="border-b">
              <DialogTitle data-testid="conclude-dialog-title">{getHeaderInfo()}</DialogTitle>
            </DialogHeader>
            <div className="mx-6 flex flex-1 flex-col overflow-hidden">
              {triggeredValidation && hasUnSignedOrUnApprovedDocument && (
                <div className="mt-4 mb-8 grid gap-1">
                  <Banner type="inline" variant="critical">
                    {t("patient.conclude.signOrApproveAllDocuments")}
                  </Banner>
                </div>
              )}
              {contextAppointment?.isCompleted === true && (
                <div className="mt-4 mb-8 grid gap-1">
                  <Banner type="inline" variant="info">
                    {t("patient.conclude.reEditWarning")}
                  </Banner>
                </div>
              )}
              <div className="grid flex-1 grid-cols-[373px_763px] gap-3 overflow-hidden">
                <ConcludeAside ref={concludeAsideRef} onRegenerationSuccess={() => setViewerKey((prev) => prev + 1)} />
                <DocumentViewer key={viewerKey} onSigningSuccess={handleSuccessfulSigning} />
              </div>
            </div>
            <DialogFooter className="mt-auto flex border-t p-6 pt-4" data-testid="conclude-dialog-footer">
              <Button
                className="px-4 py-2"
                variant="secondary"
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("check_out_continue_editing")
                  onCloseDialog()
                }}
                data-testid="continue-editing-button">
                {t("patient.conclude.continueEditing")}
              </Button>
              {renderConcludeButton()}
            </DialogFooter>
          </DialogContentCustom>
        </FormProvider>
      </Dialog>
      {showFeedbackDialog && (
        <AgentFeedbackDialog onSubmit={handleFeedbackSubmit} isLoading={isConcludingAppointment} />
      )}
    </>
  )
}

export { ConcludeDialog }
